import { fetchWithTokenRefresh, getCSRFToken } from "views_shared/app/javascript/api/withTokenRefresh";

const SUPABASE_URL = () => globalThis.Qwoted?.SUPABASE_API_EXTERNAL_URL;

// Fetch display details for a set of user_ids from the
// admin_profiles_lookup PostgREST view. Used by the picker + the
// StoryIdeaForm edit-mode hydration to populate the candidate pool
// with name / workplace / bio / avatar etc. for previously-selected
// users.
//
// The view returns one row per user_id that exists in account_users.
// Callers diff the response against the requested IDs and synthesise
// stub "(deleted user)" rows for any missing UUIDs (true deletions).
//
// Notification-eligibility is NOT included in this response — it's a
// separate concern with a separate endpoint (fetchUserNotificationEligibility
// below). Profile-display and notification-eligibility have different
// audiences and evolve on different timelines; coupling them produces
// silently-broken results (an opted-out user disappearing from a
// profile picker that should still show them).
export async function fetchUserProfiles(ids) {
  if (!ids || ids.length === 0) return [];

  const idList = ids.map((id) => encodeURIComponent(id)).join(",");
  const url = `${SUPABASE_URL()}/rest/v1/admin_profiles_lookup?id=in.(${idList})&select=*`;

  // fetchWithTokenRefresh transparently handles 401 → refresh → retry,
  // so the picker doesn't get knocked over by an expired access_token
  // mid-session.
  const response = await fetchWithTokenRefresh(url, {
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) throw new Error(`Profile lookup failed (${response.status})`);
  return response.json();
}

// Fetch per-(user, notification) eligibility from the
// admin_user_notification_eligibility PostgREST RPC. Returns
// `{ user_id, is_eligible, reasons[] }` per input user_id —
// `reasons` is empty when eligible and otherwise names each failing
// predicate ('account_disabled', 'legals_not_accepted',
// 'not_onboarded', 'under_audit', 'opted_out_or_unknown_notification').
//
// Parameterised by notificationDbName so it can decorate ANY
// notification's recipient set — not just StoryIdea. Pass e.g.
// 'media_new_story_idea' to check rollup eligibility for the
// StoryIdea email (see the RPC for the supported db_name list and
// why it's a CASE arm rather than a JOIN today).
export async function fetchUserNotificationEligibility(ids, notificationDbName) {
  if (!ids || ids.length === 0) return [];
  if (!notificationDbName) {
    throw new Error("fetchUserNotificationEligibility: notificationDbName is required");
  }

  // PostgREST RPC GET-callable because the function is `stable`. POST
  // also works; sticking with POST here so the (potentially long)
  // user_ids array goes in the body rather than a query string with
  // URL-length risk.
  const url = `${SUPABASE_URL()}/rest/v1/rpc/admin_user_notification_eligibility`;

  const response = await fetchWithTokenRefresh(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      p_user_ids: ids,
      p_notification_db_name: notificationDbName,
    }),
  });

  if (!response.ok) throw new Error(`Eligibility lookup failed (${response.status})`);
  return response.json();
}

// Hit the Rails search endpoint (Admin::Api::SearchController) for the
// recipient subject. `mode` is one of "semantic" | "by_name" | "by_outlet".
// Returns { rows: [...], pagination: {...}, funnel_counts: {...} } —
// the shape the controller wraps via Admin::Api::Search::Result.to_h.
// Each finder's row contract is documented in
// MatchFeed::Find::MediaUsers::*.
//
// Throws on non-OK responses with .retryAdvised + .status set so the
// picker UI can pick the right affordance (mirrors the expert_picker
// api.js pattern).
// One page returns the whole candidate pool. The picker has no "load more"
// control — it fetches wide and the results toolbar post-filters client-side
// — so per_page is set to the finders' candidate ceiling (mirrors the Ruby
// MediaUsers::Semantic / ByName DEFAULT_PER_PAGE = DEFAULT_LIMIT).
const DEFAULT_PER_PAGE = 1000;

export async function searchRecipients({ mode, query, page = 1, perPage = DEFAULT_PER_PAGE, signal }) {
  // Server runs every search wide: full MAX_SEARCH_DISTANCE for semantic,
  // no login_recency filter. max_distance and login_recency are post-
  // filters applied in the picker UI's results toolbar so admins can
  // retune without firing another OpenAI call.
  const params = new URLSearchParams({
    subject: "recipient",
    mode,
    q: query,
    login_recency: "any",
  });

  // by_outlet doesn't paginate (SQL caps at 20 outlets × 50 reporters).
  // RecipientSearchParams ignores page/per_page in that mode anyway, but
  // not sending them keeps the URL clean.
  if (mode !== "by_outlet") {
    params.append("page", String(page));
    params.append("per_page", String(perPage));
  }

  const response = await fetchWithTokenRefresh(`/admin/api/search?${params.toString()}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    signal,
  });

  if (!response.ok) {
    let body = null;
    try {
      body = await response.json();
    } catch (_) {
      // non-JSON error body (e.g. 5xx HTML)
    }
    const message = body?.error || `Search failed (${response.status})`;
    const error = new Error(message);
    error.retryAdvised = Boolean(body?.retry_advised);
    error.status = response.status;
    throw error;
  }

  const json = await response.json();
  return json.data?.recipient || { rows: [], pagination: {} };
}

// --- AI vet (async) ---------------------------------------------------------
//
// The vet endpoint is asynchronous: the server splits the pool into batches,
// runs the OpenAI calls on Sidekiq workers (off the web dynos), and the client
// polls for incremental verdicts. Three calls replace the old synchronous
// vetRecipients:
//   createVetJob  → POST   → { job_id }       (202; enqueues the batch jobs)
//   pollVetJob    → GET     → { status, progress, verdicts, failed, model }
//   cancelVetJob  → DELETE  → 204             (stops not-yet-started batches)
//
// useVerdictBatching owns the create→poll→merge lifecycle; these are thin
// transport. Throw on non-OK with .retryAdvised + .status + .notFound so the
// hook can pick the right affordance (mirrors searchRecipients).

function vetError(body, status, fallback) {
  const error = new Error(body?.error || `${fallback} (${status})`);
  error.retryAdvised = Boolean(body?.retry_advised);
  error.status = status;
  error.notFound = status === 404;
  return error;
}

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch (_) {
    return null; // non-JSON error body (e.g. 5xx HTML)
  }
}

// Kick off a vet run. Body: { story_idea: {title, news_hook, topics},
// candidates: [...] }. Resolves to { job_id } the client then polls.
export async function createVetJob({ storyIdea, candidates }) {
  const response = await fetchWithTokenRefresh("/admin/api/recipient_vettings", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCSRFToken(),
    },
    body: JSON.stringify({ story_idea: storyIdea, candidates }),
  });

  if (!response.ok) {
    throw vetError(await parseJsonSafe(response), response.status, "Vet failed to start");
  }
  return response.json();
}

// Poll a vet run. Returns the server snapshot:
//   { status: "running"|"completed"|"failed"|"cancelled",
//     progress: { completed, total },
//     verdicts: [{ user_id, verdict, reason }],   // cumulative, streams in
//     failed:   [{ candidate_ids: [...], error }],
//     model }
// A 404 (TTL expired / unknown id) throws with .notFound so the hook can stop
// polling gracefully.
export async function pollVetJob(jobId, { signal } = {}) {
  const response = await fetchWithTokenRefresh(`/admin/api/recipient_vettings/${encodeURIComponent(jobId)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    signal,
  });

  if (!response.ok) {
    throw vetError(await parseJsonSafe(response), response.status, "Vet poll failed");
  }
  return response.json();
}

// Request cancellation. Best-effort (fire-and-forget from the caller's POV):
// the server sets a flag that stops queued batches issuing OpenAI calls.
export async function cancelVetJob(jobId) {
  await fetchWithTokenRefresh(`/admin/api/recipient_vettings/${encodeURIComponent(jobId)}`, {
    method: "DELETE",
    headers: { "X-CSRF-Token": getCSRFToken() },
  });
}
